// Shared period model + chart helpers, ported from the mobile app's
// lib/meterCharts.ts (pure functions only — the RN-specific bar/point pixel
// math stayed behind; recharts handles layout itself). Keep the period/bucket
// logic in sync with mobile so both apps show identical windows.

import type { MeterBucket, MeterGranularity } from './api';
import i18n from './i18n';
import { wallOf } from './zone';

// Short month / weekday names are NOT translation keys: we localize them
// through the Intl API in the current UI language so we get proper locale
// forms (Norwegian/Swedish/Estonian abbreviations) for free.
function buildMonthShort(): string[] {
  const fmt = new Intl.DateTimeFormat(i18n.language, { month: 'short' });
  // Day 15 of each month avoids any timezone roll-over off the 1st.
  return Array.from({ length: 12 }, (_, m) => fmt.format(new Date(Date.UTC(2021, m, 15))));
}

function buildMonthLong(): string[] {
  const fmt = new Intl.DateTimeFormat(i18n.language, { month: 'long' });
  return Array.from({ length: 12 }, (_, m) => fmt.format(new Date(Date.UTC(2021, m, 15))));
}

function buildWeekdayShort(): string[] {
  const fmt = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
  // 2021-08-01 is a Sunday, so index 0 = Sun … index 6 = Sat, matching the
  // getUTCDay() ordering callers rely on.
  return Array.from({ length: 7 }, (_, d) => fmt.format(new Date(Date.UTC(2021, 7, 1 + d))));
}

// Live, locale-aware arrays. Kept as mutable arrays (not getters) so callers
// can index them (MONTH_SHORT[m]); we rebuild the contents in place on
// language change so the reference stays stable while the values track the
// active locale.
const MONTH_SHORT: string[] = buildMonthShort();
const MONTH_LONG: string[] = buildMonthLong();
const WEEKDAY_SHORT: string[] = buildWeekdayShort();

function refreshShortNames() {
  MONTH_SHORT.splice(0, MONTH_SHORT.length, ...buildMonthShort());
  MONTH_LONG.splice(0, MONTH_LONG.length, ...buildMonthLong());
  WEEKDAY_SHORT.splice(0, WEEKDAY_SHORT.length, ...buildWeekdayShort());
}
i18n.on('languageChanged', refreshShortNames);

export { MONTH_SHORT, MONTH_LONG, WEEKDAY_SHORT };

// Dates always read European — day before month, 24-hour clock — whatever
// the UI language: Intl's 'en' would print "Sep 1, 2026, 02:05 PM".
const pad2 = (n: number): string => String(n).padStart(2, '0');

export type ViewMode = 'day' | 'week' | 'month' | 'year';

export const VIEW_MODE_KEYS: ViewMode[] = ['day', 'week', 'month', 'year'];

const VIEW_MODE_LABEL_KEYS: Record<ViewMode, string> = {
  day: 'common.day',
  week: 'common.week',
  month: 'common.month',
  year: 'common.year',
};

export function viewModeLabel(view: ViewMode): string {
  return i18n.t(VIEW_MODE_LABEL_KEYS[view]);
}

// Pick the chart bucket size for each view. Year → one bar per month
// (12 points), month → one per day (~30), week → one per day (7), day →
// one per hour (24). The backend respects whatever we send.
export const GRANULARITY_BY_VIEW: Record<ViewMode, MeterGranularity> = {
  day: 'hour',
  week: 'day',
  month: 'day',
  year: 'month',
};

// meterTypeRank buckets a meter into a coarse type order: water → heat →
// energy → temperature → everything else. Lower = first.
export function meterTypeRank(icon: string | null | undefined): number {
  const stripped = (icon ?? '').replace(/^mdi-?/, '');
  if (stripped.includes('water')) return 0;
  if (stripped.includes('flame') || stripped.includes('fire')) return 1;
  if (stripped.includes('flash') || stripped.includes('bolt') || stripped.includes('power'))
    return 2;
  if (stripped.includes('thermometer')) return 3;
  return 9;
}

export function formatValue(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return parseFloat(v.toFixed(3)).toString();
}

// startOfPeriod returns the UTC-anchored Date that opens the calendar
// period containing `t` for the given view mode. Day = midnight; week =
// the Monday at 00:00; month = the 1st at 00:00; year = Jan 1 at 00:00.
// Period math runs on wall dates (see lib/zone.ts): convert with fromWall
// before sending a bound to the API.
export function startOfPeriod(t: Date, view: ViewMode): Date {
  const y = t.getUTCFullYear();
  const m = t.getUTCMonth();
  const d = t.getUTCDate();
  if (view === 'day') return new Date(Date.UTC(y, m, d));
  if (view === 'month') return new Date(Date.UTC(y, m, 1));
  if (view === 'year') return new Date(Date.UTC(y, 0, 1));
  // week: anchor at Monday. JS getUTCDay → 0 (Sun) … 6 (Sat); we want
  // (day + 6) % 7 days back to land on Monday.
  const dow = t.getUTCDay();
  const back = (dow + 6) % 7;
  return new Date(Date.UTC(y, m, d - back));
}

// endOfPeriod returns the exclusive upper bound — the start of the next
// period. AddDate-style stepping keeps month/year math calendar-aware.
export function endOfPeriod(start: Date, view: ViewMode): Date {
  const y = start.getUTCFullYear();
  const m = start.getUTCMonth();
  const d = start.getUTCDate();
  if (view === 'day') return new Date(Date.UTC(y, m, d + 1));
  if (view === 'week') return new Date(Date.UTC(y, m, d + 7));
  if (view === 'month') return new Date(Date.UTC(y, m + 1, 1));
  return new Date(Date.UTC(y + 1, 0, 1));
}

// stepPeriod moves the anchor forward (offset = +1) or back (-1) by one
// view-sized step.
export function stepPeriod(anchor: Date, view: ViewMode, offset: number): Date {
  const y = anchor.getUTCFullYear();
  const m = anchor.getUTCMonth();
  const d = anchor.getUTCDate();
  if (view === 'day') return new Date(Date.UTC(y, m, d + offset));
  if (view === 'week') return new Date(Date.UTC(y, m, d + 7 * offset));
  if (view === 'month') return new Date(Date.UTC(y, m + offset, 1));
  return new Date(Date.UTC(y + offset, 0, 1));
}

// periodTitle formats the currently-shown period for the prev/next navigator.
export function periodTitle(anchor: Date, view: ViewMode): string {
  const start = startOfPeriod(anchor, view);
  const y = start.getUTCFullYear();
  const m = start.getUTCMonth();
  const d = start.getUTCDate();
  if (view === 'day') return `${WEEKDAY_SHORT[start.getUTCDay()]} ${d} ${MONTH_SHORT[m]} ${y}`;
  if (view === 'week')
    return periodRangeLabel(start.toISOString(), endOfPeriod(start, view).toISOString());
  if (view === 'month') return `${MONTH_LONG[m]} ${y}`;
  return String(y);
}

// periodRangeLabel formats the inclusive day-span a period covers — e.g.
// "16 – 22 Jun" or, across a month boundary, "30 Jun – 6 Jul". `toISO` is the
// *exclusive* upper bound, so the last covered day is one day before it.
// Formatted in UTC to match the period anchors.
export function periodRangeLabel(fromISO: string, toISO: string): string {
  const from = new Date(fromISO);
  const to = new Date(new Date(toISO).getTime() - 24 * 60 * 60 * 1000);
  const fm = from.getUTCMonth();
  const fd = from.getUTCDate();
  const tm = to.getUTCMonth();
  const td = to.getUTCDate();
  return fm === tm
    ? `${fd} – ${td} ${MONTH_SHORT[fm]}`
    : `${fd} ${MONTH_SHORT[fm]} – ${td} ${MONTH_SHORT[tm]}`;
}

// ------ Custom range (the web-only "Custom" period view) ------------------
// A free [from, to] day range — both ends UTC-midnight Dates, `to` inclusive
// (the range covers all of `to`'s day). Web-only: the mobile app has no
// custom view, so these helpers have no mobile counterpart to sync with.

export type CustomRange = { from: Date; to: Date };

const DAY_MS = 24 * 60 * 60 * 1000;

// customRangeEnd returns the exclusive upper bound of a custom range — the
// start of the day after `to` — matching the [start, end) convention of the
// fixed periods.
export function customRangeEnd(range: CustomRange): Date {
  return new Date(range.to.getTime() + DAY_MS);
}

// customGranularity picks the chart bucket size for a free range by its
// span, mirroring the bucket counts of the fixed views: up to 2 days →
// hourly, up to ~3 months → daily, longer → monthly.
export function customGranularity(range: CustomRange): MeterGranularity {
  const days = Math.round((customRangeEnd(range).getTime() - range.from.getTime()) / DAY_MS);
  if (days <= 2) return 'hour';
  if (days <= 92) return 'day';
  return 'month';
}

// seedCustomRange converts the fixed period containing `anchor` into an
// inclusive day range clamped to [min, max-or-today] — the starting range
// when the user switches to the Custom view, so the switch keeps showing
// the window that was already on screen.
export function seedCustomRange(
  anchor: Date,
  view: ViewMode,
  min?: Date | null,
  max?: Date | null,
  now: Date = new Date(),
): CustomRange {
  const start = startOfPeriod(anchor, view);
  const endIncl = new Date(endOfPeriod(start, view).getTime() - DAY_MS);
  let hi = startOfPeriod(now, 'day');
  if (max && max < hi) hi = max;
  const to = endIncl > hi ? hi : endIncl;
  let from = min && start < min ? min : start;
  if (from > to) from = to;
  return { from, to };
}

// customRangeTitle formats an inclusive custom range for the toolbar —
// "3 – 12 Jun 2026", "3 Jun – 12 Jul 2026", or across a year boundary
// "20 Dec 2025 – 4 Jan 2026". Formatted in UTC like the period anchors.
export function customRangeTitle(range: CustomRange): string {
  const f = range.from;
  const t = range.to;
  const fy = f.getUTCFullYear();
  const ty = t.getUTCFullYear();
  const fm = f.getUTCMonth();
  const tm = t.getUTCMonth();
  const fd = f.getUTCDate();
  const td = t.getUTCDate();
  if (fy !== ty) return `${fd} ${MONTH_SHORT[fm]} ${fy} – ${td} ${MONTH_SHORT[tm]} ${ty}`;
  if (fm !== tm) return `${fd} ${MONTH_SHORT[fm]} – ${td} ${MONTH_SHORT[tm]} ${fy}`;
  return `${fd} – ${td} ${MONTH_SHORT[fm]} ${fy}`;
}

// bucketLabel formats one bucket's date for the chart x-axis based on the
// granularity the backend used. Hour → "HH"; day → day-of-month;
// month → short month name. Read on the project calendar (tz).
export function bucketLabel(b: MeterBucket, granularity: MeterGranularity, tz: string): string {
  const d = wallOf(b.date, tz);
  if (granularity === 'hour') return d.getUTCHours().toString().padStart(2, '0');
  if (granularity === 'month') return MONTH_SHORT[d.getUTCMonth()];
  return d.getUTCDate().toString().padStart(2, '0');
}

// tooltipDateLabel formats one bucket's date for the hover tooltip and the
// readings table. More precise than bucketLabel — includes the year for
// day/month, and the hour only for hour-granularity buckets: a day or month
// bucket has no meaningful time of day. Read on the project calendar (tz).
export function tooltipDateLabel(
  b: Pick<MeterBucket, 'date'>,
  granularity: MeterGranularity,
  tz: string,
): string {
  const d = wallOf(b.date, tz);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const h = d.getUTCHours().toString().padStart(2, '0');
  if (granularity === 'hour') return `${pad2(day)}.${pad2(m + 1)}.${y} ${h}:00`;
  if (granularity === 'month') return `${MONTH_SHORT[m]} ${y}`;
  return `${pad2(day)}.${pad2(m + 1)}.${y}`;
}

// slotKey names the calendar slot a wall date falls in at a granularity.
export function slotKey(wall: Date, granularity: MeterGranularity): string {
  const k = `${wall.getUTCFullYear()}-${wall.getUTCMonth() + 1}`;
  if (granularity === 'month') return k;
  if (granularity === 'day') return `${k}-${wall.getUTCDate()}`;
  return `${k}-${wall.getUTCDate()}-${wall.getUTCHours()}`;
}

function nextSlot(wall: Date, granularity: MeterGranularity): Date {
  const y = wall.getUTCFullYear();
  const m = wall.getUTCMonth();
  if (granularity === 'month') return new Date(Date.UTC(y, m + 1, 1));
  if (granularity === 'day') return new Date(Date.UTC(y, m, wall.getUTCDate() + 1));
  return new Date(wall.getTime() + 60 * 60 * 1000);
}

// chartSlots lays the buckets out on the project calendar, one slot per
// hour/day/month from the first bucket to the last. The backend sends no
// bucket for a slot without readings, so a day the meter stayed silent keeps
// its own (empty) place on the axis instead of the next day sliding into it.
// The edges are not padded: the axis still starts and ends with data.
export function chartSlots(
  buckets: MeterBucket[],
  granularity: MeterGranularity,
  tz: string,
): { wall: Date; bucket?: MeterBucket }[] {
  if (buckets.length === 0) return [];
  const byKey = new Map(buckets.map((b) => [slotKey(wallOf(b.date, tz), granularity), b]));
  const last = slotKey(wallOf(buckets[buckets.length - 1].date, tz), granularity);
  const out: { wall: Date; bucket?: MeterBucket }[] = [];
  // Bounded so a malformed date can never spin forever.
  for (let w = wallOf(buckets[0].date, tz); out.length < 10_000; w = nextSlot(w, granularity)) {
    const key = slotKey(w, granularity);
    out.push({ wall: w, bucket: byKey.get(key) });
    if (key === last) break;
  }
  return out;
}

// labelEvery decides how often to print a bucket label so the axis stays
// readable. With ~24 hour-buckets, every 4th is plenty; with ~30 day
// buckets, every 5th; with 12 months, every one.
export function labelEvery(granularity: MeterGranularity, count: number): number {
  if (granularity === 'month') return 1;
  return Math.max(1, Math.ceil(count / 6));
}

// niceStep rounds an arbitrary step size up to the nearest 1, 2 or 5 × 10ⁿ so
// the y-axis lands on round numbers (… 0.2, 0.5, 1, 2, 5, 10, 20 …).
function niceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  const exp = Math.floor(Math.log10(raw));
  const base = Math.pow(10, exp);
  const f = raw / base; // 1 ≤ f < 10
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * base;
}

// computeYAxis fits the y-axis to the data range with round-number gridlines
// and a full step of headroom above the tallest value. The baseline is lifted
// off zero only when the data sits in a narrow band well above zero (e.g. a
// totaliser reading), so bars that genuinely reach zero still anchor there
// honestly. Returns a recharts-ready [min, max] domain plus explicit ticks.
export function computeYAxis(values: number[]): {
  domain: [number, number];
  ticks: number[];
} {
  const SECTIONS = 4;
  const finite = values.filter((v) => Number.isFinite(v));
  const fallback = {
    domain: [0, 1] as [number, number],
    ticks: [0, 0.25, 0.5, 0.75, 1],
  };
  if (finite.length === 0) return fallback;
  const maxVal = Math.max(...finite);
  const minVal = Math.min(...finite);
  if (maxVal <= 0) return fallback;

  // Lift the baseline only when the spread is small relative to the floor —
  // otherwise anchor at 0 so the magnitude reads honestly (esp. for bars).
  const span = maxVal - minVal;
  let baseline = minVal > 0 && span < minVal ? minVal - span * 0.5 : 0;
  if (baseline < 0) baseline = 0;

  const range = maxVal - baseline || maxVal;
  const step = niceStep(range / SECTIONS);
  baseline = step * Math.floor(baseline / step); // snap baseline onto the grid
  let sections = Math.ceil((maxVal - baseline) / step);
  // Guarantee a gap between the tallest value and the top gridline.
  if (baseline + sections * step <= maxVal + step * 0.05) sections += 1;
  if (sections < 1) sections = 1;

  const ticks = Array.from({ length: sections + 1 }, (_, i) => {
    // Round away float noise (0.30000000000000004 → 0.3).
    const t = baseline + i * step;
    return parseFloat(t.toPrecision(12));
  });
  return { domain: [ticks[0], ticks[ticks.length - 1]], ticks };
}

// Relative "time ago" label for notification cards and card subtitles.
export function timeAgo(dateISO: string): string {
  const then = new Date(dateISO).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) return i18n.t('notifications.timeNow');
  if (mins < 60) return i18n.t('notifications.timeMinutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return i18n.t('notifications.timeHoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 31) return i18n.t('notifications.timeDaysAgo', { count: days });
  const months = Math.floor(days / 30.4);
  if (months < 12) return i18n.t('notifications.timeMonthsAgo', { count: months });
  return i18n.t('notifications.timeYearsAgo', { count: Math.floor(months / 12) });
}

// Absolute local date-time for "last reading" style subtitles —
// "01.09.2026 14:05".
export function formatDateTime(dateISO: string | null | undefined): string {
  const date = formatDate(dateISO);
  if (!date) return '';
  const d = new Date(dateISO as string);
  return `${date} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Absolute local date — "01.09.2026".
export function formatDate(dateISO: string | null | undefined): string {
  if (!dateISO) return '';
  const d = new Date(dateISO);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}
